import { skipToken } from '@reduxjs/toolkit/query/react';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { getBoardTemplateVisibility } from 'modules/template-editor/domain/utils/board-template-visibility';
import sortTemplates from 'modules/template-editor/domain/utils/sort-templates';
import { useGetOwnerBoardTemplatesQuery } from 'modules/template-editor/store/board-templates-api';
import { useCallback, useMemo } from 'react';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { deriveAsyncState } from 'shared/utils/async-state';

/**
 * Шаблоны трансляций текущей организации — из кэша RTK Query (`getOwnerBoardTemplates`).
 *
 * Любой экран редактора табло зовёт этот хук, а не тянет список сам: аргументы (организация + видимость по
 * роли) у всех одинаковые, значит запись кэша одна и запрос в пути один (IMGM-1566). `currentData`, а не
 * `data`: при смене организации `data` ещё держит шаблоны прежней.
 */
export function useOwnerBoardTemplates() {
  const { info } = useOrganizer();
  const isAdmin = useIsAdmin();
  const ownerId = info?.id;
  const skip = !ownerId;

  const query = useGetOwnerBoardTemplatesQuery(skip ? skipToken : { ownerId, visibility: getBoardTemplateVisibility(isAdmin) });
  const { currentData, isFetching, isError, error, refetch: refetchQuery } = query;

  const templates = useMemo(() => sortTemplates(currentData ?? []), [currentData]);

  // Ветки экрана — `.claude/docs/async-loading-states.md`: организация ещё не загружена — это ожидание
  // (`loading`), а не «шаблонов нет».
  const state = useMemo(
    () =>
      deriveAsyncState({
        itemsCount: templates.length,
        hasCurrentData: currentData !== undefined,
        isFetching,
        isPending: skip,
        isError,
      }),
    [currentData, isError, isFetching, skip, templates.length],
  );

  // RTK бросает на `refetch` незапущенного запроса — при `skip` повторять нечего. Промис запроса отдаётся
  // наружу: кнопка «Повторить» заперта до ответа (IMGM-1561).
  const refetch = useCallback(() => (skip ? undefined : refetchQuery()), [refetchQuery, skip]);

  return {
    ownerId,
    templates,
    /** Ветка экрана: загрузка / пусто / ошибка / готово (`deriveAsyncState`). */
    state,
    isError,
    error,
    refetch,
  };
}
